import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import {
  PRICEBOOK_SERVICES,
  type ServiceCategory,
  type ServiceDefinition,
} from '../lib/pricebook/pricebook';
import { usePricebookCatalogSync } from '../hooks/usePricebookCatalogSync';
import { gradientPillCss } from '../styles/gradientPill';
import { SERVICE_COPY_BY_CATEGORY } from '../content/serviceCopy';
import ServiceOfferingsCarousel from '../components/services/ServiceOfferingsCarousel';

type ServiceVisualConfig = {
  icon: string;
  heading: string;
  accentLabel: string;
  background: string;
  highlights: string[];
  image?: string;
  imageAlt?: string;
  overlays?: ReadonlyArray<{
    src: string;
    alt: string;
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
    size?: string;
    delay?: string;
  }>;
};

const PLATFORM_TOOL_LOGO_OVERLAYS: NonNullable<ServiceVisualConfig['overlays']> = [
  {
    src: '/images/awsLogo.png',
    alt: 'AWS logo',
    top: '16%',
    left: '12%',
    size: 'clamp(3.6rem, 9vw, 5.5rem)',
    delay: '0s',
  },
  {
    src: '/images/reactLogo.png',
    alt: 'React logo',
    top: '12%',
    right: '10%',
    size: 'clamp(3.4rem, 8vw, 5.2rem)',
    delay: '0.8s',
  },
  {
    src: '/images/shopifyLogo.png',
    alt: 'Shopify logo',
    bottom: '12%',
    left: '50%',
    size: 'clamp(3.8rem, 9vw, 5.7rem)',
    delay: '1.6s',
  },
];

const CREATIVE_TOOL_LOGO_OVERLAYS: NonNullable<ServiceVisualConfig['overlays']> = [
  {
    src: '/images/adobeLogo.png',
    alt: 'Adobe logo',
    top: '16%',
    left: '12%',
    size: 'clamp(3.6rem, 9vw, 5.5rem)',
    delay: '0s',
  },
  {
    src: '/images/figmaLogo.png',
    alt: 'Figma logo',
    top: '12%',
    right: '10%',
    size: 'clamp(3.4rem, 8vw, 5.2rem)',
    delay: '0.8s',
  },
  {
    src: '/images/blenderLogo.png',
    alt: 'Blender logo',
    bottom: '12%',
    left: '50%',
    size: 'clamp(3.8rem, 9vw, 5.7rem)',
    delay: '1.6s',
  },
];

const MARKETING_TOOL_LOGO_OVERLAYS: NonNullable<ServiceVisualConfig['overlays']> = [
  {
    src: '/images/ga4.png',
    alt: 'GA4 logo',
    top: '16%',
    left: '12%',
    size: 'clamp(3.6rem, 9vw, 5.5rem)',
    delay: '0s',
  },
  {
    src: '/images/semrush-Logo.png',
    alt: 'Semrush logo',
    top: '12%',
    right: '10%',
    size: 'clamp(3.4rem, 8vw, 5.2rem)',
    delay: '0.8s',
  },
  {
    src: '/images/hootsuite-logo-png_seeklogo-446074.png',
    alt: 'Hootsuite logo',
    bottom: '12%',
    left: '50%',
    size: 'clamp(3.8rem, 9vw, 5.7rem)',
    delay: '1.6s',
  },
];

const SERVICE_VISUALS: Record<ServiceCategory, ServiceVisualConfig> = {
  platform: {
    icon: '🧩',
    heading: 'Platform Build Engine',
    accentLabel: 'Execution Layer',
    background: 'linear-gradient(135deg, #1f3160 0%, #1a7fbe 45%, #69d2ff 100%)',
    highlights: [
      '20-120 delivery hours / month',
      'Roadmap + sprint execution',
      'Web, ecommerce, and integrations',
    ],
    image: '/images/platformDevelopment1.png',
    imageAlt: 'Platform development showcase',
    overlays: PLATFORM_TOOL_LOGO_OVERLAYS,
  },
  marketing: {
    icon: '📣',
    heading: 'Growth Operations',
    accentLabel: 'Demand Layer',
    background: 'linear-gradient(135deg, #222244 0%, #6f2dbd 45%, #ff7d66 100%)',
    highlights: [
      '2 to 5+ active lanes',
      'Required onboarding auto-attached',
      'Weekly alignment + performance reporting',
    ],
    image: '/images/marketing1.png',
    imageAlt: 'Managed marketing showcase',
    overlays: MARKETING_TOOL_LOGO_OVERLAYS,
  },
  assets_design: {
    icon: '🎨',
    heading: 'Creative Subscription',
    accentLabel: 'Design Layer',
    background: 'linear-gradient(135deg, #153042 0%, #1d7a74 45%, #87d37c 100%)',
    highlights: [
      'Unlimited backlog queue',
      'One ticket in progress at a time',
      '24-48 business hour triage SLA',
    ],
    image: '/images/coolShip.png',
    imageAlt: 'CyBard cool ship illustration',
    overlays: CREATIVE_TOOL_LOGO_OVERLAYS,
  },
  custom: {
    icon: '🚀',
    heading: 'Custom Engagement',
    accentLabel: 'Special Projects',
    background: 'linear-gradient(135deg, #2a1f36 0%, #8e44ad 45%, #f7a44c 100%)',
    highlights: [
      'Large-scope or non-standard asks',
      'Consultative scoping before proposal',
      'Includes rebrand, VR/AR, and specialized builds',
    ],
  },
};

const serviceSectionId = (slug: string) => `service-${slug}`;

const Page = styled.main`
  background:
    radial-gradient(1200px 720px at 18% 8%, rgba(56, 189, 248, 0.32) 0%, rgba(56, 189, 248, 0) 62%),
    radial-gradient(1100px 760px at 84% 24%, rgba(168, 85, 247, 0.34) 0%, rgba(168, 85, 247, 0) 60%),
    radial-gradient(1200px 740px at 20% 50%, rgba(244, 114, 182, 0.3) 0%, rgba(244, 114, 182, 0) 62%),
    radial-gradient(1050px 700px at 82% 74%, rgba(251, 146, 60, 0.28) 0%, rgba(251, 146, 60, 0) 60%),
    radial-gradient(1200px 760px at 26% 94%, rgba(45, 212, 191, 0.3) 0%, rgba(45, 212, 191, 0) 64%),
    linear-gradient(180deg, #efecf0 0%, #efecf0 100%);
  color: ${({ theme }) => theme.colors.gray900};
`;

const IntroSection = styled.section`
  min-height: calc(92svh - 72px);
  padding: clamp(2.4rem, 6vw, 6rem) 1.1rem;
  display: flex;
  align-items: center;
  background: transparent;
  color: #17212f;

  @media (max-width: 680px) {
    min-height: auto;
    padding: 5.5rem 0.9rem 2.2rem;
  }
`;

const IntroInner = styled.div`
  width: min(1200px, 100%);
  margin: 0 auto;
  display: grid;
  gap: clamp(1.2rem, 2.8vw, 2.6rem);
  grid-template-columns: 1.2fr 1fr;
  align-items: stretch;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const IntroCopy = styled.article`
  display: grid;
  align-content: center;
  gap: 0.95rem;
`;

const IntroEyebrow = styled.span`
  width: fit-content;
  border: 1px solid rgba(23, 33, 47, 0.14);
  background: rgba(255, 255, 255, 0.42);
  border-radius: 999px;
  padding: 0.3rem 0.68rem;
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 800;
  color: #17212f;
`;

const IntroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2rem, 5vw, 4.1rem);
  line-height: 0.98;
  letter-spacing: -0.02em;
  color: #17212f;
`;

const IntroLead = styled.p`
  margin: 0;
  font-size: clamp(1rem, 2vw, 1.22rem);
  line-height: 1.55;
  max-width: 62ch;
  color: #465263;
`;

const IntroHint = styled.p`
  margin: 0;
  font-size: 0.92rem;
  color: #5a6677;
`;

const IntroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.68rem;
  margin-top: 0.28rem;

  @media (max-width: 680px) {
    width: 100%;
    flex-wrap: nowrap;
    gap: 0.42rem;
    align-items: stretch;
  }
`;

const IntroServiceLink = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px dashed rgba(23, 33, 47, 0.3);
  background: transparent;
  color: #17212f;
  font-weight: 700;
  padding: 0.66rem 1rem;
  font-size: 0.9rem;

  &:hover {
    border-color: rgba(23, 33, 47, 0.5);
    background: rgba(255, 255, 255, 0.22);
  }

  @media (max-width: 680px) {
    flex: 1 1 0;
    min-width: 0;
    padding: 0.58rem 0.45rem;
    font-size: 0.76rem;
    line-height: 1.2;
    text-align: center;
    justify-content: center;
    white-space: normal;
    text-wrap: balance;
  }
`;

const IntroVisualWrap = styled.aside`
  position: relative;
  min-height: 480px;
  display: grid;
  place-items: center;
  overflow: visible;

  @media (max-width: 980px) {
    min-height: 420px;
  }

  @media (max-width: 680px) {
    min-height: 360px;
  }
`;

const IntroVisual = styled.img`
  width: min(100%, 620px);
  max-height: min(72vh, 540px);
  object-fit: contain;
  display: block;
  border-radius: 20px;
  filter: drop-shadow(0 24px 40px rgba(23, 33, 47, 0.18));
  -webkit-mask-image: radial-gradient(
    ellipse 76% 74% at 50% 50%,
    rgba(0, 0, 0, 1) 44%,
    rgba(0, 0, 0, 0.92) 58%,
    rgba(0, 0, 0, 0.58) 72%,
    rgba(0, 0, 0, 0.18) 86%,
    transparent 100%
  );
  mask-image: radial-gradient(
    ellipse 76% 74% at 50% 50%,
    rgba(0, 0, 0, 1) 44%,
    rgba(0, 0, 0, 0.92) 58%,
    rgba(0, 0, 0, 0.58) 72%,
    rgba(0, 0, 0, 0.18) 86%,
    transparent 100%
  );
`;

const ServiceBand = styled.section`
  min-height: calc(100svh - 72px);
  display: flex;
  align-items: center;
  padding: clamp(2.75rem, 5vw, 5rem) 1.1rem;
  background: transparent;

  @media (max-width: 980px) {
    min-height: auto;
    padding: 2.2rem 0.95rem;
  }
`;

const BandInner = styled.div<{ $reverse: boolean }>`
  width: min(1200px, 100%);
  margin: 0 auto;
  display: grid;
  gap: clamp(1.25rem, 3vw, 3rem);
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: stretch;

  ${({ $reverse }) =>
    $reverse
      ? css`
          & > article {
            order: 2;
          }

          & > aside {
            order: 1;
          }
        `
      : null}

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    align-items: start;
    min-height: auto;

    & > aside {
      order: 1;
    }

    & > article {
      order: 2;
    }
  }
`;

const Copy = styled.article`
  padding: clamp(0.4rem, 1.2vw, 0.8rem) clamp(0.1rem, 0.5vw, 0.25rem);
  display: grid;
  gap: 0.82rem;
  align-content: center;
`;

const Eyebrow = styled.span<{ $dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.32rem 0.65rem;
  border-radius: 999px;
  font-size: 0.76rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${({ $dark }) => ($dark ? 'rgba(23, 33, 47, 0.95)' : 'rgba(255, 255, 255, 0.95)')};
  background: ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.46)' : 'rgba(255, 255, 255, 0.16)')};
  border: 1px solid ${({ $dark }) => ($dark ? 'rgba(23, 33, 47, 0.14)' : 'rgba(255, 255, 255, 0.35)')};
`;

const Title = styled.h2<{ $dark?: boolean }>`
  margin: 0;
  font-size: clamp(1.8rem, 3.8vw, 3rem);
  line-height: 1.06;
  letter-spacing: -0.02em;
  color: ${({ $dark }) => ($dark ? '#17212f' : '#fff')};
  text-shadow: ${({ $dark }) => ($dark ? 'none' : '0 10px 24px rgba(0, 0, 0, 0.25)')};
`;

const Subtitle = styled.p<{ $dark?: boolean }>`
  margin: 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: ${({ $dark }) => ($dark ? 'rgba(23, 33, 47, 0.9)' : 'rgba(255, 255, 255, 0.96)')};
`;

const Description = styled.p<{ $dark?: boolean }>`
  margin: 0;
  font-size: 1rem;
  line-height: 1.55;
  color: ${({ $dark }) => ($dark ? '#465263' : 'rgba(255, 255, 255, 0.94)')};
  max-width: 56ch;
`;

const CopyPointList = styled.div`
  display: grid;
  gap: 0.78rem;
  max-width: 58ch;
`;

const CopyPointItem = styled.div`
  padding: 0;
  display: grid;
  gap: 0.28rem;
`;

const CopyPointTitle = styled.h3`
  margin: 0;
  font-size: 0.96rem;
  line-height: 1.25;
  color: #17212f;
`;

const CopyPointBody = styled.p`
  margin: 0;
  font-size: 0.94rem;
  line-height: 1.5;
  color: #526071;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.62rem;
  margin-top: 0.22rem;
`;

const PrimaryLink = styled(Link)`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  ${gradientPillCss};
`;

const subtlePulse = keyframes`
  0%,
  100% {
    transform: translateY(0) scale(1);
  }

  50% {
    transform: translateY(-4px) scale(1.04);
  }
`;

const Visual = styled.aside<{ $hasImage?: boolean }>`
  border-radius: 28px;
  padding: clamp(1rem, 2.3vw, 1.5rem);
  background: rgba(255, 255, 255, 0.46);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(23, 33, 47, 0.12);
  color: #17212f;
  box-shadow: 0 18px 45px rgba(23, 33, 47, 0.08);
  display: grid;
  align-content: ${({ $hasImage }) => ($hasImage ? 'center' : 'end')};
  min-height: clamp(320px, 56vh, 520px);
  overflow: hidden;
  position: relative;

  @media (max-width: 680px) {
    border-radius: 20px;
    min-height: clamp(260px, 45vh, 360px);
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 999px;
    pointer-events: none;
  }

  &::before {
    width: 240px;
    height: 240px;
    top: -90px;
    right: -65px;
    background: rgba(214, 160, 89, 0.12);
  }

  &::after {
    width: 180px;
    height: 180px;
    left: -50px;
    bottom: -60px;
    background: rgba(23, 33, 47, 0.06);
  }
`;

const VisualImage = styled.img`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 520px;
  max-height: min(72vh, 440px);
  margin: 0 auto;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 18px 28px rgba(23, 33, 47, 0.16));
`;

const ImageVisual = styled.aside`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: clamp(320px, 56vh, 520px);
  padding: 0;
  background: transparent;
  border: 0;
  overflow: visible;
  isolation: isolate;

  @media (max-width: 680px) {
    min-height: clamp(260px, 45vh, 360px);
  }
`;

const VisualOverlayBadge = styled.div<{
  $top?: string;
  $right?: string;
  $bottom?: string;
  $left?: string;
  $size?: string;
}>`
  position: absolute;
  top: ${({ $top }) => $top ?? 'auto'};
  right: ${({ $right }) => $right ?? 'auto'};
  bottom: ${({ $bottom }) => $bottom ?? 'auto'};
  left: ${({ $left }) => $left ?? 'auto'};
  width: ${({ $size }) => $size ?? 'clamp(3.5rem, 8vw, 5rem)'};
  aspect-ratio: 1;
  padding: 0.7rem;
  border-radius: 22px;
  box-shadow:
    0 18px 34px rgba(23, 33, 47, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(10px);
  pointer-events: none;
  z-index: 2;

  ${({ $left, $right }) =>
    $left === '50%' && !$right
      ? css`
          transform: translateX(-50%);
        `
      : null}

  @media (max-width: 680px) {
    padding: 0.55rem;
    border-radius: 18px;
  }
`;

const VisualOverlayLogo = styled.img<{ $delay?: string }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  animation: ${subtlePulse} 3.8s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay ?? '0s'};
  filter: drop-shadow(0 8px 16px rgba(23, 33, 47, 0.12));
`;

const VisualHeader = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.72rem;
  margin-bottom: 0.85rem;
`;

const VisualIcon = styled.span`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-size: 1.5rem;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(23, 33, 47, 0.12);
  box-shadow: 0 10px 24px rgba(23, 33, 47, 0.08);
`;

const VisualLabel = styled.div`
  display: grid;
  gap: 0.1rem;

  strong {
    font-size: 1.03rem;
    letter-spacing: 0.01em;
    color: #17212f;
  }

  small {
    font-size: 0.82rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #667281;
  }
`;

const VisualList = styled.ul`
  position: relative;
  z-index: 1;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.55rem;
`;

const VisualListItem = styled.li`
  border: 1px solid rgba(23, 33, 47, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.58);
  padding: 0.62rem 0.72rem;
  font-size: 0.9rem;
  color: #334155;
`;

function getPrimaryAction(service: ServiceDefinition): { label: string; to: string } {
  return {
    label: 'Build Your Package',
    to: `/services/${service.slug}`,
  };
}

function getServiceCopy(category: ServiceCategory) {
  switch (category) {
    case 'platform':
      return SERVICE_COPY_BY_CATEGORY.platform;
    case 'marketing':
      return SERVICE_COPY_BY_CATEGORY.marketing;
    case 'assets_design':
      return SERVICE_COPY_BY_CATEGORY.assets_design;
    default:
      return null;
  }
}

export default function ServicesPage() {
  const pricebookVersion = usePricebookCatalogSync();

  const services = useMemo<readonly ServiceDefinition[]>(
    () => {
      void pricebookVersion;
      return [
        PRICEBOOK_SERVICES.platform,
        PRICEBOOK_SERVICES.marketing,
        PRICEBOOK_SERVICES.assets_design,
        // PRICEBOOK_SERVICES.custom,
      ];
    },
    [pricebookVersion],
  );

  return (
    <Page>
      <IntroSection>
        <IntroInner>
          <IntroCopy>
            <IntroEyebrow>Service Menu</IntroEyebrow>
            <IntroTitle>Choose The Service Track That Fits Your Growth Stage</IntroTitle>
            <IntroLead>
              CyBard offers multiple service lines so you can choose exactly what your business needs right now,
              then expand later without changing partners.
            </IntroLead>
            <IntroHint>
              Browse each service below, click to learn more, and build your package through the funnel.
            </IntroHint>
            <IntroActions>
              <IntroServiceLink to="/services/platform-development">Platform Development</IntroServiceLink>
              <IntroServiceLink to="/services/managed-marketing">Managed Marketing</IntroServiceLink>
              <IntroServiceLink to="/services/assets-design">Assets &amp; Design</IntroServiceLink>
            </IntroActions>
          </IntroCopy>

          <IntroVisualWrap>
            <IntroVisual src="/images/controlCenter.png" alt="CyBard control center interface" />
          </IntroVisualWrap>
        </IntroInner>
      </IntroSection>

      <ServiceOfferingsCarousel />

      {services.map((service, index) => {
        const visual = SERVICE_VISUALS[service.category];
        const serviceCopy = getServiceCopy(service.category);
        const primaryAction = getPrimaryAction(service);
        const reverse = index % 2 === 1;

        return (
          <ServiceBand
            key={service.slug}
            id={serviceSectionId(service.slug)}
          >
            <BandInner $reverse={reverse}>
              <Copy>
                <Eyebrow $dark>{serviceCopy?.eyebrow ?? service.title}</Eyebrow>
                <Title $dark>{serviceCopy?.title ?? service.title}</Title>
                <Subtitle $dark>{service.subtitle}</Subtitle>
                <Description $dark>{serviceCopy?.lead ?? service.description}</Description>
                {serviceCopy ? (
                  <CopyPointList>
                    {serviceCopy.points.map((point) => (
                      <CopyPointItem key={point.title}>
                        <CopyPointTitle>{point.title}</CopyPointTitle>
                        <CopyPointBody>{point.body}</CopyPointBody>
                      </CopyPointItem>
                    ))}
                  </CopyPointList>
                ) : null}
                <CtaRow>
                  <PrimaryLink to={primaryAction.to}>
                    {primaryAction.label}
                  </PrimaryLink>
                </CtaRow>
              </Copy>

              {visual.image ? (
                <ImageVisual>
                  <VisualImage src={visual.image} alt={visual.imageAlt ?? visual.heading} />
                  {visual.overlays?.map((overlay) => (
                    <VisualOverlayBadge
                      key={overlay.src}
                      $top={overlay.top}
                      $right={overlay.right}
                      $bottom={overlay.bottom}
                      $left={overlay.left}
                      $size={overlay.size}
                    >
                      <VisualOverlayLogo src={overlay.src} alt={overlay.alt} $delay={overlay.delay} />
                    </VisualOverlayBadge>
                  ))}
                </ImageVisual>
              ) : (
                <Visual>
                  <VisualHeader>
                    <VisualIcon aria-hidden>{visual.icon}</VisualIcon>
                    <VisualLabel>
                      <strong>{visual.heading}</strong>
                      <small>{visual.accentLabel}</small>
                    </VisualLabel>
                  </VisualHeader>
                  <VisualList>
                    {visual.highlights.map((highlight) => (
                      <VisualListItem key={highlight}>{highlight}</VisualListItem>
                    ))}
                  </VisualList>
                </Visual>
              )}
            </BandInner>
          </ServiceBand>
        );
      })}
    </Page>
  );
}
